import { PreparedItem } from '@/models/item.models';
import {
  PreparedGanttInitiative,
  PreparedGanttMilestone,
} from '@/models/gantt.models';
import dayjs from '@/utils/dayjs';
import { Initiative, ItemType } from '@/api';

/**
 * Returns the identifying value for a gantt item.
 * For folders and work items, this is the number.
 * For milestones and initiatives, this is the id.
 */
export function getGanttItemIdentifier(
  item: PreparedGanttInitiative | PreparedGanttMilestone | PreparedItem
) {
  let identifier = item.id;
  if (
    item.itemType === ItemType.FOLDER ||
    item.itemType === ItemType.WORKITEM
  ) {
    identifier = item.number;
  }
  return identifier;
}

/**
 * Returns a hashmap for efficient dependency chain lookup for items and milestones.
 */
export function getDependencyChainLookup(
  items: PreparedItem[],
  milestones: PreparedGanttMilestone[]
) {
  const itemsMap: Record<
    PreparedItem['number'] | PreparedGanttMilestone['id'],
    PreparedGanttMilestone | PreparedItem
  > = {};
  [...milestones, ...items].forEach((item) => {
    const key = getGanttItemIdentifier(item);
    itemsMap[key] = item;
  });

  const itemDepMap: Record<
    PreparedItem['number'] | PreparedGanttMilestone['id'],
    {
      dependents: (PreparedGanttMilestone | PreparedItem)[];
      dependencies: (PreparedGanttMilestone | PreparedItem)[];
    }
  > = {};

  [...items, ...milestones].forEach((item) => {
    const dependentNumberOrId = getGanttItemIdentifier(item);

    item.relationships?.forEach((relationship) => {
      if (relationship.type !== 'blocked_by') return;
      const dependencyNumberOrId = relationship.referenceId;
      if (!itemDepMap[dependencyNumberOrId])
        itemDepMap[dependencyNumberOrId] = {
          dependents: [],
          dependencies: [],
        };
      if (!itemDepMap[dependentNumberOrId])
        itemDepMap[dependentNumberOrId] = {
          dependents: [],
          dependencies: [],
        };

      const dependency = itemsMap[dependencyNumberOrId];
      itemDepMap[dependentNumberOrId].dependencies.push(dependency);

      const dependent = item;
      itemDepMap[dependencyNumberOrId].dependents.push(dependent);
    });
  });

  return itemDepMap;
}

export function getHierarchyLookup(items: PreparedItem[]) {
  const itemsMap: Record<PreparedItem['number'], PreparedItem> = {};
  items.forEach((item) => {
    const key = getGanttItemIdentifier(item);
    itemsMap[key] = item;
  });
  const itemHierarchyMap: Record<
    PreparedItem['number'],
    {
      parent: PreparedItem | null;
      children: PreparedItem[];
    }
  > = {};

  items.forEach((child) => {
    const parent = child.parentNumber ? itemsMap[child.parentNumber] : null;

    const childKey = getGanttItemIdentifier(child);
    if (!itemHierarchyMap[childKey])
      itemHierarchyMap[childKey] = {
        parent: null,
        children: [],
      };

    if (parent) {
      const parentKey = getGanttItemIdentifier(parent);
      if (!itemHierarchyMap[parentKey])
        itemHierarchyMap[parentKey] = {
          parent: null,
          children: [],
        };

      itemHierarchyMap[parentKey].children.push(child);
      itemHierarchyMap[childKey].parent = parent;
    }
  });

  return itemHierarchyMap;
}

export const getInitiativeInferredDates = (
  initiative: Initiative,
  itemsMap: Map<PreparedItem['number'], PreparedItem>
) => {
  const items: PreparedItem[] = initiative.itemNumbers
    .map((itemNumber) => itemsMap.get(itemNumber))
    .filter((item) => !!item) as PreparedItem[];

  const allDates = items.flatMap((item) => {
    const isExpandable = item.canHaveChildren;
    return [
      item.startDate,
      item.dueDate,
      isExpandable ? item.minDate : null,
      isExpandable ? item.maxDate : null,
    ]
      .filter((date) => !!date)
      .map((date) => dayjs(date));
  });

  let startDate = dayjs.min(allDates);
  if (!startDate) startDate = dayjs(); // if no items, use today
  startDate = startDate.startOf('day');

  let endDate = dayjs.max(allDates);
  if (!endDate) endDate = startDate; // if no items, use start date (which would either be today or the earliest item start date)
  endDate = endDate.startOf('day');

  // flip if the dates are backwards
  if (startDate.isAfter(endDate)) {
    [startDate, endDate] = [endDate, startDate];
  }

  return {
    startDate: startDate.toISOString(),
    dueDate: endDate.toISOString(),
  };
};
